import React, { useEffect, useMemo, useState } from "react";
import Header from "../layouts/Header";
import Footer from "../layouts/Footer";
import { Badge, Button, Card, Col, Form, Row, Table } from "react-bootstrap";
import MissingGradesModal from "./MissingGradesModal";
import { Link } from "react-router-dom";
import { getToken } from '../services/authorize';
import axiosInstance from "../services/axiosInterceptor";
import { useLoading } from '../contexts/LoadingContext';
import { useAuth } from '../services/authProvider';

const API = process.env.REACT_APP_API;

// สถานะของวิชา × ห้อง — server คำนวณมาให้แล้ว (reportsModel.gradeSubmissionRows)
const STATUS_META = {
  none:     { label: 'ยังไม่เริ่ม', bg: 'danger',  hint: 'ยังไม่มีนักเรียนคนใดในห้องมีคะแนนวิชานี้' },
  partial:  { label: 'ส่งไม่ครบ',  bg: 'warning', hint: 'เริ่มกรอกแล้ว แต่ยังมีนักเรียนติด ร (คะแนนเก็บ/สอบยังไม่ครบทุกช่อง) หรือยังไม่มีคะแนนเลย' },
  complete: { label: 'ครบ',        bg: 'success', hint: 'นักเรียนทุกคนในห้องมีเกรดและไม่ติด ร' },
};
const ALL_STATUSES = ['none', 'partial', 'complete'];

// กลุ่มของครู — ดูจากทุกห้องที่ครูคนนั้นสอนรวมกัน
const TEACHER_GROUPS = {
  not_started:      { label: 'ยังไม่เริ่มป้อนเกรด',            color: 'danger',    icon: 'ri-close-circle-line',    hint: 'ทุกวิชาทุกห้องยังไม่มีคะแนน' },
  some_rooms:       { label: 'ป้อนยังไม่ครบทุกห้อง',            color: 'orange',    icon: 'ri-door-open-line',       hint: 'กรอกไปแล้วบางห้อง ยังมีห้องที่ยังไม่เริ่ม' },
  missing_students: { label: 'ป้อนทุกห้องแล้ว แต่ขาดบางคน',     color: 'warning',   icon: 'ri-user-unfollow-line',   hint: 'เริ่มครบทุกห้อง แต่ยังมีนักเรียนติด ร หรือไม่มีคะแนน' },
  complete:         { label: 'ส่งครบแล้ว',                       color: 'success',   icon: 'ri-checkbox-circle-line', hint: 'ทุกห้องครบทุกคน' },
};
const GROUP_ORDER = ['not_started', 'some_rooms', 'missing_students', 'complete'];

// กลุ่มสาระจากตัวอักษรแรกของรหัสวิชา — แบบเดียวกับหน้าจุดเด่นด้านการเรียน (talentController.CATEGORIES)
const SUBJECT_GROUPS = [
  { key: 'thai',    prefix: 'ท', label: 'ภาษาไทย' },
  { key: 'math',    prefix: 'ค', label: 'คณิตศาสตร์' },
  { key: 'science', prefix: 'ว', label: 'วิทยาศาสตร์ฯ' },
  { key: 'social',  prefix: 'ส', label: 'สังคมศึกษา' },
  { key: 'pe',      prefix: 'พ', label: 'สุขศึกษา/พละ' },
  { key: 'art',     prefix: 'ศ', label: 'ศิลปะ' },
  { key: 'career',  prefix: 'ง', label: 'การงานอาชีพ' },
  { key: 'english', prefix: 'อ', label: 'ภาษาอังกฤษ' },
];
const PREFIX_TO_GROUP = Object.fromEntries(SUBJECT_GROUPS.map(g => [g.prefix, g.key]));
// รหัสวิชาที่ไม่อยู่ใน 8 กลุ่ม (ภาษาต่างประเทศอื่น กิจกรรม ฯลฯ) รวมไว้ที่ "อื่นๆ"
const subjectGroupOf = (r) => PREFIX_TO_GROUP[String(r.sub_subid || '').trim().charAt(0)] || 'other';

const roleLabelOf = (posIds = []) => {
  const has = (p) => posIds.includes(p);
  if (has(3) || has(4) || has(99)) return 'หัวหน้าวิชาการสามัญ';
  if (has(10) || has(11)) return 'ฝ่ายวัดผลและประเมินผล';
  if (has(30)) return 'หัวหน้ากลุ่มสาระ';
  return 'หัวหน้าวิชาการสามัญ';
};

const roomLabel = (r) => `ม.${r.class_room}`;

const teacherGroupOf = (t) => {
  if (t.none === t.rows.length) return 'not_started';
  if (t.none > 0) return 'some_rooms';
  if (t.partial > 0) return 'missing_students';
  return 'complete';
};

// ป้ายห้องหนึ่งห้อง: สีตามสถานะ + จำนวนที่มีเกรด/ทั้งห้อง กดดูรายชื่อคนที่ขาดได้
const RoomChip = ({ r, onOpen }) => {
  const meta = STATUS_META[r.status];
  const clickable = r.status !== 'complete';
  return (
    <button
      type="button"
      className={`btn btn-sm btn-outline-${meta.bg} d-inline-flex align-items-center gap-1 py-0 px-2`}
      style={{ cursor: clickable ? 'pointer' : 'default' }}
      onClick={() => clickable && onOpen(r)}
      title={clickable ? 'ดูรายชื่อนักเรียนที่ยังไม่มีเกรด' : meta.hint}
    >
      <span className="fw-semibold">{roomLabel(r)}</span>
      <span className="fs-xs">{r.graded_count}/{r.total_students}</span>
      {r.waiting_count > 0 && <span className="fs-xs">· ร {r.waiting_count}</span>}
      {r.status === 'partial' && r.empty_count > 0 && <span className="fs-xs">· ว่าง {r.empty_count}</span>}
    </button>
  );
};

const TeacherCard = ({ t, open, onToggle, onOpenRoom }) => {
  const g = TEACHER_GROUPS[t.group];
  return (
    <div className={`border rounded border-start border-4 border-${g.color === 'orange' ? 'warning' : g.color}`}>
      <div className="d-flex flex-wrap align-items-center gap-2 p-3" role="button" onClick={onToggle}>
        <i className={`ri-arrow-${open ? 'down' : 'right'}-s-line`} />
        <div className="fw-semibold me-auto">{t.t_name || '(ไม่ระบุครู)'}</div>
        <span className="fs-sm text-muted">{t.subjects.length} วิชา · {t.rows.length} ห้อง</span>
        {t.none > 0 && <Badge bg="danger">ยังไม่เริ่ม {t.none} ห้อง</Badge>}
        {t.partial > 0 && <Badge bg="warning" text="dark">ไม่ครบ {t.partial} ห้อง</Badge>}
        {t.complete > 0 && <Badge bg="success">ครบ {t.complete} ห้อง</Badge>}
        {t.missing > 0 && <span className="fs-sm text-danger">ขาด {t.missing} คน</span>}
      </div>
      {open && (
        <div className="border-top px-3 pb-3">
          {t.subjects.map(s => (
            <div key={s.key} className="pt-2">
              <div className="fs-sm">
                <span className="text-muted">{s.sub_subid}</span> {s.sub_subname}
                {s.sub_type === 'extra' && <span className="text-muted"> (เพิ่มเติม)</span>}
                <span className="text-muted"> · ภาค {s.sub_semester}</span>
              </div>
              <div className="d-flex flex-wrap gap-1 mt-1">
                {s.rows.map(r => <RoomChip key={r.teach_id} r={r} onOpen={onOpenRoom} />)}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default function GradeSubmissionReport() {
  const currentSkin = localStorage.getItem('skin-mode') ? 'dark' : '';
  const [skin, setSkin] = useState(currentSkin);
  const { setLoading } = useLoading();
  const { posIds = [] } = useAuth();

  const switchSkin = (s) => {
    const tables = document.getElementsByTagName('table');
    for (const t of tables) {
      const theads = t.querySelectorAll('thead');
      if (s === 'dark') {
        for (const th of theads) { th.classList.remove('table-secondary'); th.classList.add('table-primary'); }
        t.classList.add('table-dark');
      } else {
        t.classList.remove('table-dark');
        for (const th of theads) { th.classList.remove('table-primary'); th.classList.add('table-secondary'); }
      }
    }
  };

  const [yearList, setYearList] = useState([]);
  const [yearId, setYearId]     = useState(null);
  const [semester, setSemester] = useState('all');     // 'all' | '1' | '2'
  const [rows, setRows]         = useState([]);
  const [tab, setTab]           = useState('teacher'); // 'teacher' | 'subject'
  const [groupFilter, setGroupFilter] = useState(['not_started', 'some_rooms', 'missing_students']);
  const [statusFilter, setStatusFilter] = useState(['none', 'partial']);
  const [search, setSearch]     = useState('');
  const [dept, setDept]         = useState(() => sessionStorage.getItem('gradeSubmission_dept') || 'all');
  const [openTeachers, setOpenTeachers] = useState(() => new Set());
  const [missing, setMissing]   = useState(null);      // แถววิชา × ห้องที่เปิดดูรายชื่อ

  useEffect(() => { switchSkin(skin); }, [skin, tab, rows, dept]);

  useEffect(() => {
    axiosInstance.get(`${API}/getStudyYear`, { headers: { authorization: `Bearer ${getToken()}` } })
      .then(res => {
        const years = res.data || [];
        setYearList(years);
        if (years.length > 0) setYearId(years[0].year_id);
      })
      .catch(() => {});
  }, []);

  // โหลดทุกสถานะครั้งเดียว — การจัดกลุ่มครูต้องเห็นทุกห้องของครูคนนั้น ตัวกรองทำฝั่งหน้าจอ
  useEffect(() => {
    if (!yearId) return;
    setLoading(true);
    const sem = semester === 'all' ? '' : `/${semester}`;
    axiosInstance.get(`${API}/getGradeSubmissionByYear/${yearId}${sem}`, { headers: { authorization: `Bearer ${getToken()}` } })
      .then(res => { setRows(res.data || []); setOpenTeachers(new Set()); })
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [yearId, semester]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { sessionStorage.setItem('gradeSubmission_dept', dept); }, [dept]);

  const rowsWithGroup = useMemo(() => rows.map(r => ({ ...r, dept: subjectGroupOf(r) })), [rows]);

  // จำนวนวิชา × ห้องที่ยังไม่ครบ ต่อกลุ่มสาระ — ขึ้นบนปุ่มกรอง
  const deptCounts = useMemo(() => {
    const c = { all: { total: 0, pending: 0 } };
    rowsWithGroup.forEach(r => {
      if (!c[r.dept]) c[r.dept] = { total: 0, pending: 0 };
      const pending = r.status !== 'complete' ? 1 : 0;
      c[r.dept].total += 1; c[r.dept].pending += pending;
      c.all.total += 1; c.all.pending += pending;
    });
    return c;
  }, [rowsWithGroup]);

  const deptOptions = useMemo(() => [
    { key: 'all', label: 'ทุกกลุ่มสาระ' },
    ...SUBJECT_GROUPS,
    ...(deptCounts.other ? [{ key: 'other', label: 'อื่นๆ' }] : []),
  ], [deptCounts]);

  // ทั้งหน้า (KPI กลุ่มครู ตาราง) คิดจากกลุ่มสาระที่เลือก — สถานะของครูจึงดูเฉพาะวิชาในกลุ่มนั้น
  const deptRows = useMemo(() => dept === 'all' ? rowsWithGroup : rowsWithGroup.filter(r => r.dept === dept),
    [rowsWithGroup, dept]);

  const kpi = useMemo(() => {
    const c = { total: deptRows.length, none: 0, partial: 0, complete: 0, waiting: 0, empty: 0 };
    deptRows.forEach(r => {
      c[r.status] += 1;
      if (r.status === 'partial') { c.waiting += r.waiting_count; c.empty += r.empty_count; }
    });
    return c;
  }, [deptRows]);

  const teachers = useMemo(() => {
    const map = new Map();
    deptRows.forEach(r => {
      const key = r.t_id ?? 'none';
      if (!map.has(key)) map.set(key, { t_id: key, t_name: r.t_name, rows: [], subjectMap: new Map(), none: 0, partial: 0, complete: 0, missing: 0 });
      const t = map.get(key);
      t.rows.push(r);
      t[r.status] += 1;
      if (r.status === 'partial') t.missing += r.waiting_count + r.empty_count;
      const sk = `${r.sub_id}-${r.sub_semester}`;
      if (!t.subjectMap.has(sk)) t.subjectMap.set(sk, { key: sk, sub_subid: r.sub_subid, sub_subname: r.sub_subname, sub_type: r.sub_type, sub_semester: r.sub_semester, rows: [] });
      t.subjectMap.get(sk).rows.push(r);
    });
    return [...map.values()].map(t => {
      const subjects = [...t.subjectMap.values()];
      subjects.forEach(s => s.rows.sort((a, b) => (a.tb_class_c_id - b.tb_class_c_id) || (a.r_no - b.r_no)));
      return { ...t, subjects, group: teacherGroupOf(t) };
    });
  }, [deptRows]);

  const groupCounts = useMemo(() => {
    const c = Object.fromEntries(GROUP_ORDER.map(g => [g, 0]));
    teachers.forEach(t => { c[t.group] += 1; });
    return c;
  }, [teachers]);

  const q = search.trim().toLowerCase();
  const matches = (r) => !q
    || String(r.t_name || '').toLowerCase().includes(q)
    || String(r.sub_subid || '').toLowerCase().includes(q)
    || String(r.sub_subname || '').toLowerCase().includes(q);

  const visibleTeachers = useMemo(() => teachers
    .filter(t => groupFilter.includes(t.group))
    .filter(t => !q || String(t.t_name || '').toLowerCase().includes(q) || t.rows.some(matches))
    .sort((a, b) => (GROUP_ORDER.indexOf(a.group) - GROUP_ORDER.indexOf(b.group))
      || ((b.none + b.partial) - (a.none + a.partial))
      || String(a.t_name || '').localeCompare(String(b.t_name || ''), 'th')),
  [teachers, groupFilter, q]); // eslint-disable-line react-hooks/exhaustive-deps

  const visibleRows = useMemo(() => deptRows.filter(r => statusFilter.includes(r.status) && matches(r)),
    [deptRows, statusFilter, q]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleIn = (setter) => (v) => setter(prev => prev.includes(v) ? prev.filter(x => x !== v) : [...prev, v]);
  const toggleGroup = toggleIn(setGroupFilter);
  const toggleStatus = toggleIn(setStatusFilter);
  const toggleTeacher = (id) => setOpenTeachers(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  const openMissing = (row) => setMissing(row);

  const kpiCards = [
    { label: 'วิชา × ห้อง ทั้งหมด', value: kpi.total,    color: 'primary', icon: 'ri-book-2-line' },
    { label: 'ส่งครบ',              value: kpi.complete, color: 'success', icon: 'ri-checkbox-circle-line' },
    { label: 'ส่งไม่ครบ',           value: kpi.partial,  color: 'warning', icon: 'ri-error-warning-line',
      sub: kpi.partial > 0 ? `ติด ร ${kpi.waiting} คน · ไม่มีคะแนน ${kpi.empty} คน` : null },
    { label: 'ยังไม่เริ่ม',          value: kpi.none,     color: 'danger',  icon: 'ri-close-circle-line' },
  ];

  return (
    <React.Fragment>
      <Header onSkin={setSkin} />
      <div className="main main-app p-3 p-lg-4">

        <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-4">
          <div>
            <ol className="breadcrumb fs-sm mb-1">
              <li className="breadcrumb-item"><Link to="#">{roleLabelOf(posIds)}</Link></li>
              <li className="breadcrumb-item active">รายงานการส่งเกรด</li>
            </ol>
            <h4 className="main-title mb-0">รายงานการส่งเกรด</h4>
          </div>
          <div className="d-flex flex-wrap align-items-center gap-2">
            <select
              className="form-select form-select-sm"
              style={{ minWidth: 150 }}
              value={yearId || ''}
              onChange={e => setYearId(e.target.value ? Number(e.target.value) : null)}
            >
              {yearList.map(y => (
                <option key={y.year_id} value={y.year_id}>ปีการศึกษา {y.year_year}</option>
              ))}
            </select>
            <div className="btn-group btn-group-sm">
              {[['all','ทุกภาคเรียน'],['1','ภาคเรียน 1'],['2','ภาคเรียน 2']].map(([v, label]) => (
                <button
                  key={v}
                  className={`btn btn${semester === v ? '' : '-outline'}-primary`}
                  onClick={() => setSemester(v)}
                >{label}</button>
              ))}
            </div>
          </div>
        </div>

        {/* กรองตามกลุ่มสาระ — ตัวเลขแดง = วิชา × ห้องที่ยังไม่ครบในกลุ่มนั้น */}
        <div className="d-flex flex-wrap gap-1 mb-3">
          {deptOptions.map(o => {
            const c = deptCounts[o.key] || { total: 0, pending: 0 };
            const on = dept === o.key;
            return (
              <Button
                key={o.key}
                size="sm"
                variant={on ? 'primary' : 'outline-primary'}
                onClick={() => setDept(o.key)}
                disabled={o.key !== 'all' && c.total === 0}
                className="d-inline-flex align-items-center gap-1"
              >
                {o.label}
                {c.pending > 0 && <Badge bg={on ? 'light' : 'danger'} text={on ? 'danger' : undefined} pill>{c.pending}</Badge>}
              </Button>
            );
          })}
        </div>

        <Row className="g-3 mb-3">
          {kpiCards.map(k => (
            <Col key={k.label} xs={6} xl={3}>
              <Card className={`card-one border-${k.color} border-start border-4 h-100`}>
                <Card.Body className="p-3">
                  <div className={`text-${k.color} mb-1`}><i className={`${k.icon} fs-40 lh-1`}></i></div>
                  <h6 className="fw-semibold text-dark mb-1">{k.label}</h6>
                  <h3 className={`fw-bold text-${k.color} mb-0`}>{k.value}</h3>
                  {k.sub && <div className="fs-xs text-muted mt-1">{k.sub}</div>}
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>

        {/* ความหมายของสถานะ */}
        <Card className="card-one mb-3">
          <Card.Body className="p-3 fs-sm">
            <div className="fw-semibold mb-1"><i className="ri-information-line me-1" />นับอย่างไร</div>
            <div className="d-flex flex-column gap-1">
              {ALL_STATUSES.map(s => (
                <div key={s}><Badge bg={STATUS_META[s].bg} className="me-2">{STATUS_META[s].label}</Badge>{STATUS_META[s].hint}</div>
              ))}
              <div className="text-muted">
                นับต่อ <b>วิชา × ห้อง</b> · "ร" = กรอกคะแนนแล้วบางช่องแต่ยังไม่ครบทุกช่อง · "ว่าง" = ยังไม่มีคะแนนเลย ·
                ไม่นับนักเรียนที่จำหน่ายแล้วหรือย้ายไปห้องอื่น · กดที่ห้องเพื่อดูรายชื่อนักเรียนที่ยังไม่มีเกรด
              </div>
            </div>
          </Card.Body>
        </Card>

        <Card className="card-one">
          <Card.Header className="d-flex flex-wrap align-items-center gap-2">
            <div className="btn-group btn-group-sm">
              <button className={`btn btn${tab === 'teacher' ? '' : '-outline'}-primary`} onClick={() => setTab('teacher')}>
                <i className="ri-user-line me-1" />ตามครู
              </button>
              <button className={`btn btn${tab === 'subject' ? '' : '-outline'}-primary`} onClick={() => setTab('subject')}>
                <i className="ri-table-line me-1" />ตามวิชา × ห้อง
              </button>
            </div>
            <Form.Control
              size="sm"
              className="ms-auto"
              style={{ maxWidth: 260 }}
              placeholder="ค้นหาครู / รหัสวิชา / ชื่อวิชา"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </Card.Header>

          {tab === 'teacher' ? (
            <Card.Body className="p-3">
              <Row className="g-2 mb-3">
                {GROUP_ORDER.map(gk => {
                  const g = TEACHER_GROUPS[gk];
                  const on = groupFilter.includes(gk);
                  const color = g.color === 'orange' ? 'warning' : g.color;
                  return (
                    <Col key={gk} xs={6} lg={3}>
                      <div
                        role="button"
                        onClick={() => toggleGroup(gk)}
                        className={`border rounded p-2 h-100 border-${color} ${on ? `bg-${color} bg-opacity-10` : 'opacity-50'}`}
                        title={g.hint}
                      >
                        <div className="d-flex align-items-center gap-2">
                          <Form.Check type="checkbox" checked={on} readOnly className="mb-0" />
                          <i className={`${g.icon} text-${color}`} />
                          <span className="fs-sm fw-semibold">{g.label}</span>
                        </div>
                        <div className="d-flex align-items-baseline gap-1 mt-1">
                          <span className={`fs-4 fw-bold text-${color}`}>{groupCounts[gk]}</span>
                          <span className="fs-xs text-muted">คน · {g.hint}</span>
                        </div>
                      </div>
                    </Col>
                  );
                })}
              </Row>

              <div className="d-flex align-items-center gap-2 mb-2">
                <span className="fs-sm text-muted">แสดง {visibleTeachers.length} คน</span>
                <Button size="sm" variant="link" className="p-0 ms-auto"
                  onClick={() => setOpenTeachers(new Set(visibleTeachers.map(t => t.t_id)))}>ขยายทั้งหมด</Button>
                <Button size="sm" variant="link" className="p-0"
                  onClick={() => setOpenTeachers(new Set())}>ย่อทั้งหมด</Button>
              </div>

              {visibleTeachers.length === 0 ? (
                <div className="text-center text-muted py-4">
                  <i className="ri-inbox-line fs-3 d-block mb-1"></i>
                  {groupFilter.length === 0 ? 'กรุณาเลือกกลุ่มอย่างน้อย 1 กลุ่ม' : 'ไม่มีครูตามตัวกรอง'}
                </div>
              ) : (
                <div className="d-flex flex-column gap-2">
                  {visibleTeachers.map(t => (
                    <TeacherCard
                      key={t.t_id}
                      t={t}
                      open={openTeachers.has(t.t_id)}
                      onToggle={() => toggleTeacher(t.t_id)}
                      onOpenRoom={openMissing}
                    />
                  ))}
                </div>
              )}
            </Card.Body>
          ) : (
            <Card.Body className="p-0">
              <div className="d-flex flex-wrap align-items-center gap-3 px-3 py-2 border-bottom">
                {ALL_STATUSES.map(s => (
                  <Form.Check
                    key={s}
                    type="checkbox"
                    id={`status-${s}`}
                    className="mb-0"
                    checked={statusFilter.includes(s)}
                    onChange={() => toggleStatus(s)}
                    label={<><Badge bg={STATUS_META[s].bg} className="fs-xs">{STATUS_META[s].label}</Badge> <span className="fs-xs text-muted">{kpi[s]}</span></>}
                  />
                ))}
                <span className="fs-sm text-muted ms-auto">{visibleRows.length} รายการ</span>
              </div>
              {visibleRows.length === 0 ? (
                <div className="text-center text-muted py-4">
                  <i className="ri-inbox-line fs-3 d-block mb-1"></i>
                  {statusFilter.length === 0 ? 'กรุณาเลือกสถานะอย่างน้อย 1 รายการ' : 'ไม่มีข้อมูลตามตัวกรอง'}
                </div>
              ) : (
                <div style={{ maxHeight: 640, overflowY: 'auto' }}>
                  <Table responsive hover size="sm" className="mb-0">
                    <thead className="table-secondary sticky-top">
                      <tr>
                        <th className="text-center" style={{ width: 50 }}>#</th>
                        <th>วิชา</th>
                        <th>ครูผู้สอน</th>
                        <th className="text-center">ห้อง</th>
                        <th className="text-center">ภาค</th>
                        <th className="text-center">มีเกรด</th>
                        <th className="text-center">ติด ร</th>
                        <th className="text-center">ไม่มีคะแนน</th>
                        <th className="text-center">สถานะ</th>
                        <th />
                      </tr>
                    </thead>
                    <tbody>
                      {visibleRows.map((r, i) => {
                        const meta = STATUS_META[r.status];
                        return (
                          <tr key={r.teach_id}>
                            <td className="text-center">{i + 1}</td>
                            <td className="fs-sm">
                              <span className="text-muted">{r.sub_subid}</span> {r.sub_subname}
                              {r.sub_type === 'extra' && <span className="text-muted"> (เพิ่มเติม)</span>}
                            </td>
                            <td className="fs-sm">{r.t_name}</td>
                            <td className="text-center text-nowrap">{roomLabel(r)}</td>
                            <td className="text-center">{r.sub_semester}</td>
                            <td className="text-center text-nowrap">{r.graded_count}/{r.total_students}</td>
                            <td className="text-center">{r.waiting_count || <span className="text-muted">-</span>}</td>
                            <td className="text-center">{r.empty_count || <span className="text-muted">-</span>}</td>
                            <td className="text-center"><Badge bg={meta.bg} className="fs-xs">{meta.label}</Badge></td>
                            <td className="text-center">
                              {r.status !== 'complete' && (
                                <Button size="sm" variant="link" className="p-0 text-nowrap" onClick={() => openMissing(r)}>ดูรายชื่อ</Button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                </div>
              )}
            </Card.Body>
          )}
        </Card>

        <MissingGradesModal row={missing} onHide={() => setMissing(null)} />

        <Footer />
      </div>
    </React.Fragment>
  );
}
